import React from 'react';
import { Helmet } from 'react-helmet';
import './css/globals.css';
import './css/header.css';
import './css/footer.css';
import './css/page.css';
import './css/legal.css';
import Header from './sections/Header';
import Footer from './sections/Footer';

const PAGE_URL = 'https://imperialpdg.com.au/return-policy/';
const PAGE_TITLE = 'Returns & Refund Policy | Imperial PDG';
const PAGE_DESCRIPTION = 'Returns & Refund Policy for Imperial PDG water filtration products, including change of mind, faulty products and installation services.';

const ReturnPolicyPage = () => {

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />
        <meta name="robots" content="noindex, follow" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:site_name" content="Imperial PDG" />
        <meta property="og:locale" content="en_AU" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            'name': PAGE_TITLE,
            'description': PAGE_DESCRIPTION,
            'url': PAGE_URL,
            'isPartOf': { '@type': 'WebSite', 'name': 'Imperial PDG', 'url': 'https://imperialpdg.com.au/' }
          })}
        </script>
      </Helmet>

      <div className="returnpolicy">
        <Header />

        <section className="legal-content">
          <div className="container boxed">
            <h1>Returns &amp; Refund Policy</h1>
            <h2>Water Filtration Products</h2>

            <p>
              At Imperial PDG, our water filtration products are supplied directly by our
              licensed plumbers, who also install the system at your property. Because our
              products are delivered specifically for your installation appointment, we have
              the following returns policy.
            </p>

            <h3>Change of Mind</h3>
            <p>We do not accept returns or provide refunds for:</p>
            <ul>
              <li>Change of mind.</li>
              <li>Ordering the incorrect product.</li>
              <li>Purchasing a product that is no longer required.</li>
              <li>Incorrect selection made by the customer after the order has been confirmed.</li>
            </ul>
            <p>
              Please ensure you have selected the correct product before placing your order.
              If you are unsure which system is right for your home, our team is happy to
              provide advice before purchase.
            </p>

            <h3>Faulty Products</h3>
            <p>
              If a product is faulty due to a manufacturing defect, please contact us as soon
              as possible. Where appropriate, Imperial PDG will inspect the product and, if
              confirmed to be faulty, will provide a remedy in accordance with Australian
              Consumer Law. Depending on the nature of the fault, this may include repair,
              replacement or a refund. (
              <a
                href="https://www.accc.gov.au/business/problem-with-a-product-or-service-you-sold/repair-replace-refund-cancel"
                target="_blank"
                rel="noopener noreferrer"
              >
                ACCC
              </a>
              )
            </p>

            <h3>Conditions for Returns</h3>
            <p>To be eligible for a return where applicable:</p>
            <ul>
              <li>The product must be unused and uninstalled.</li>
              <li>The product must remain in its original unopened packaging.</li>
              <li>Proof of purchase must be provided.</li>
              <li>The return must first be authorised by Imperial PDG.</li>
            </ul>
            <p>
              Products that have been opened, installed, modified, damaged through misuse, or
              handled contrary to the manufacturer's instructions cannot be returned unless
              they are faulty under Australian Consumer Law. (
              <a
                href="https://www.accc.gov.au/business/problem-with-a-product-or-service-you-sold/repair-replace-refund-cancel"
                target="_blank"
                rel="noopener noreferrer"
              >
                ACCC
              </a>
              )
            </p>

            <h3>Installation Services</h3>
            <p>
              Once a product has been installed, it forms part of the completed plumbing
              installation and cannot be returned simply because you have changed your mind or
              selected the wrong product.
            </p>
            <p>
              If there is an issue with the installation or the supplied product, please
              contact Imperial PDG immediately so we can assess the matter and arrange an
              appropriate solution.
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ReturnPolicyPage;
